import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://usfansspreadsheet.link';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);

    const articleUrl = article ? `${SITE_URL}/artykul/${article.slug}` : `${SITE_URL}/`;

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', articleUrl);
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = articleUrl;
      document.head.appendChild(link);
    }
  }, [slug, article]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center px-4">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Artykuł nie został znaleziony</h1>
          <Link to="/" className="px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
            <i className="ri-arrow-left-line"></i>
            Powrót na stronę główną
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Strona główna', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: article.title, item: `${SITE_URL}/artykul/${article.slug}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: { '@type': 'Organization', name: 'USFans Spreadsheet' },
    datePublished: article.date,
    dateModified: article.date,
    publisher: { '@type': 'Organization', name: 'USFans Spreadsheet' },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/artykul/${article.slug}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  const catColorClasses: Record<string, string> = {
    primary: 'bg-primary-100 text-primary-700',
    accent: 'bg-accent-100 text-accent-700',
    secondary: 'bg-secondary-100 text-secondary-700',
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <nav className="sticky top-0 z-50 bg-background-50/98 backdrop-blur-md border-b-2 border-background-300/70 shadow-sm">
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-heading text-xl font-bold text-foreground-900 whitespace-nowrap cursor-pointer hover:text-primary-600 transition-colors">
              USFans Spreadsheet
            </Link>
            <div className="flex items-center gap-2 md:gap-3">
              <Link to="/" className="hidden sm:inline-flex px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Strona główna
              </Link>
              <Link to="/artykul/usfans-spreadsheet-poradnik-zakupowy" className="hidden md:inline-flex px-4 py-2 rounded-full border border-foreground-300 text-foreground-600 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Poradnik zakupowy
              </Link>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="px-4 py-2 rounded-full border border-primary-500 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-all duration-300 whitespace-nowrap cursor-pointer">
                usfans spreadsheet
              </a>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Kup teraz
              </a>
            </div>
          </div>
        </div>
      </nav>

      <nav className="w-full px-4 md:px-8 lg:px-16 max-w-4xl mx-auto py-3" aria-label="Breadcrumb">
        <ol className="flex items-center flex-wrap gap-2 text-xs text-foreground-400">
          <li><Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Strona główna</Link></li>
          <li><i className="ri-arrow-right-s-line text-[10px]"></i></li>
          <li className="text-foreground-600 font-medium line-clamp-1">{article.title}</li>
        </ol>
      </nav>

      <article className="w-full px-4 md:px-8 lg:px-16 max-w-4xl mx-auto py-6 md:py-10">
        <header className="mb-8 md:mb-12">
          <div className="flex items-center gap-2 mb-4">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${catColorClasses[article.categoryColor] || catColorClasses.primary}`}>
              {article.category}
            </span>
            <time dateTime={article.date} className="text-xs text-foreground-400">
              Ostatnia aktualizacja: {new Date(article.date).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}
            </time>
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight">
            {article.title}
          </h1>
          <p className="text-foreground-500 mt-4 text-sm md:text-base leading-relaxed max-w-2xl">
            {article.description}
          </p>
          <div className="flex items-center gap-3 mt-6 pt-6 border-t border-background-200/70">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-user-line text-primary-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-800">Zespół USFans Spreadsheet</p>
              <p className="text-xs text-foreground-400">Eksperci od arkuszy kalkulacyjnych</p>
            </div>
          </div>
        </header>

        <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        </div>

        <div className="prose-custom">
          {article.sections.map((section, idx) => (
            <section key={idx} className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">{section.heading}</h2>
              {section.content.map((paragraph, pIdx) => (
                <p key={pIdx} className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">{paragraph}</p>
              ))}
            </section>
          ))}

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Najczęściej zadawane pytania</h2>
            <div className="space-y-4 mt-4">
              {article.faq.map((item, idx) => (
                <div key={idx}>
                  <h3 className="font-semibold text-foreground-800 text-sm md:text-base mb-2">{item.q}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Podsumowanie</h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              USFans Spreadsheet to wszechstronne i niezawodne narzędzie, które sprawdzi się w rękach zarówno początkujących, jak i zaawansowanych użytkowników. Mamy nadzieję, że ten artykuł pomógł Ci lepiej zrozumieć możliwości i zalety USFans Spreadsheet. Zachęcamy do dalszego eksplorowania naszych zasobów i kategorii produktów.
            </p>
          </section>
        </div>

        <div className="mt-10 pt-8 border-t border-background-200/70">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-team-line text-primary-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-800">Zespół USFans Spreadsheet</p>
                <p className="text-xs text-foreground-400">Eksperci od arkuszy kalkulacyjnych z wieloletnim doświadczeniem w branży analityki danych i optymalizacji procesów biznesowych.</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-foreground-400">Udostępnij:</span>
              <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-foreground-600 text-sm"></i>
              </button>
              <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
                <i className="ri-facebook-line text-foreground-600 text-sm"></i>
              </button>
              <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
                <i className="ri-link text-foreground-600 text-sm"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-primary-50 border border-primary-100">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-heading font-bold text-foreground-900 text-lg mb-1">Chcesz dowiedzieć się więcej?</h3>
              <p className="text-foreground-600 text-sm">Wróć do strony głównej, aby przeglądać kategorie produktów i zasoby USFans Spreadsheet.</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="px-5 py-2.5 rounded-full bg-foreground-900 text-white text-sm font-medium hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Strona główna
              </Link>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Kup teraz
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <h3 className="font-heading font-bold text-foreground-900 text-lg mb-5">Inne artykuły</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {articles.filter(a => a.slug !== article.slug).slice(0, 4).map((a) => (
              <Link
                key={a.slug}
                to={`/artykul/${a.slug}`}
                className="group flex items-start gap-3 p-4 rounded-xl border border-background-200/70 hover:border-primary-200 bg-background-50 transition-all duration-300 cursor-pointer"
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={a.image} alt={a.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="min-w-0">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium mb-1 ${catColorClasses[a.categoryColor] || catColorClasses.primary}`}>{a.category}</span>
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">{a.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </article>

      <footer className="bg-foreground-900 text-white">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-4xl mx-auto py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="font-heading text-lg font-bold whitespace-nowrap cursor-pointer hover:text-white/80 transition-colors">
              USFans Spreadsheet
            </Link>
            <div className="flex items-center gap-6 flex-wrap justify-center">
              <Link to="/" className="text-white/50 text-xs hover:text-white/80 transition-colors duration-200 cursor-pointer">Strona główna</Link>
              <Link to="/artykul/usfans-spreadsheet-poradnik-zakupowy" className="text-white/50 text-xs hover:text-white/80 transition-colors duration-200 cursor-pointer">Poradnik zakupowy</Link>
              <Link to="/artykul/czy-usfans-spreadsheet-jest-bezpieczny" className="text-white/50 text-xs hover:text-white/80 transition-colors duration-200 cursor-pointer">Bezpieczeństwo</Link>
              <a href="#" className="text-white/50 text-xs hover:text-white/80 transition-colors duration-200">Polityka prywatności</a>
            </div>
          </div>
          <div className="border-t border-white/10 mt-6 pt-6 text-center">
            <p className="text-white/40 text-xs">&copy; {new Date().getFullYear()} USFans Spreadsheet. Wszelkie prawa zastrzeżone.</p>
          </div>
        </div>
      </footer>
    </>
  );
}